import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, UserRound } from "lucide-react";
import {
  createProvider,
  createSchedule,
  deleteSchedule,
  getProvider,
  getSchedulingErrorMessage,
  replaceWeeklyHours,
  setProviderServices,
  updateProvider,
} from "../../../api/schedulingApi";
import { Alert, Field, Modal, WEEKDAYS, btnPrimary, btnSecondary, card, formatDate, hhmm, inputClass } from "./ui";

const EMPTY_PROVIDER = { name: "", title: "", phone: "", slot_minutes: 15, is_active: true };

function ProviderForm({ open, initial, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_PROVIDER);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_PROVIDER, ...initial, title: initial?.title || "", phone: initial?.phone || "" });
      setError("");
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await onSave({
        name: form.name.trim(),
        title: form.title.trim() || null,
        phone: form.phone.trim() || null,
        slot_minutes: Number(form.slot_minutes),
        is_active: form.is_active,
      });
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not save provider."));
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <Modal open={open} title={initial?.id ? "Edit provider" : "Add provider"} onClose={onClose}>
      <form className="space-y-4" onSubmit={submit}>
        <Alert>{error}</Alert>
        <Field label="Name"><input required className={inputClass} value={form.name} onChange={set("name")} placeholder="Dr. Sharma / Priya / Chair 2" /></Field>
        <Field label="Title"><input className={inputClass} value={form.title} onChange={set("title")} placeholder="Dentist, Senior stylist…" /></Field>
        <Field label="Phone"><input className={inputClass} value={form.phone} onChange={set("phone")} /></Field>
        <Field label="Slot length (minutes)" hint="Bookings start every N minutes within working hours.">
          <input required type="number" min="5" step="5" className={inputClass} value={form.slot_minutes} onChange={set("slot_minutes")} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.is_active} onChange={set("is_active")} /> Accepting bookings
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="submit" className={btnPrimary}>Save</button>
        </div>
      </form>
    </Modal>
  );
}

function WeeklyHoursEditor({ botId, provider, schedules, onSaved }) {
  const toState = () =>
    WEEKDAYS.map((_, day) =>
      schedules
        .filter((s) => s.weekday === day)
        .sort((a, b) => a.start_time.localeCompare(b.start_time))
        .map((s) => ({ start: hhmm(s.start_time), end: hhmm(s.end_time) }))
    );
  const [days, setDays] = useState(toState);
  const [message, setMessage] = useState({ kind: "", text: "" });
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDays(toState()), [schedules]);

  const update = (day, index, key, value) =>
    setDays((all) => all.map((shifts, d) => (d !== day ? shifts : shifts.map((s, i) => (i === index ? { ...s, [key]: value } : s)))));
  const addShift = (day) =>
    setDays((all) => all.map((shifts, d) => (d !== day ? shifts : [...shifts, shifts.length ? { start: "17:00", end: "20:00" } : { start: "10:00", end: "13:00" }])));
  const removeShift = (day, index) => setDays((all) => all.map((shifts, d) => (d !== day ? shifts : shifts.filter((_, i) => i !== index))));
  const copyToAll = (day) => setDays((all) => all.map((shifts, d) => (d === day || d === 6 ? shifts : all[day].map((s) => ({ ...s })))));

  const save = async () => {
    setSaving(true);
    setMessage({ kind: "", text: "" });
    try {
      const hours = days.flatMap((shifts, weekday) => shifts.map((s) => ({ weekday, start_time: s.start, end_time: s.end })));
      await replaceWeeklyHours(botId, provider.id, hours);
      setMessage({ kind: "info", text: "Weekly hours saved." });
      onSaved();
    } catch (err) {
      setMessage({ kind: "error", text: getSchedulingErrorMessage(err, "Could not save hours.") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-900">Weekly hours</h4>
        <button type="button" className={btnPrimary} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save hours"}</button>
      </div>
      <Alert kind={message.kind || "error"}>{message.text}</Alert>
      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
        {WEEKDAYS.map((label, day) => (
          <div key={label} className="flex flex-wrap items-center gap-3 px-3 py-2">
            <div className="w-24 text-sm font-semibold text-slate-700">{label}</div>
            <div className="flex flex-1 flex-wrap items-center gap-2">
              {days[day].length === 0 && <span className="text-xs text-slate-400">Closed</span>}
              {days[day].map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-lg bg-slate-50 px-2 py-1">
                  <input type="time" className="rounded border border-slate-200 px-1 py-0.5 text-xs" value={s.start} onChange={(e) => update(day, i, "start", e.target.value)} aria-label={`${label} shift ${i + 1} start`} />
                  <span className="text-xs text-slate-400">–</span>
                  <input type="time" className="rounded border border-slate-200 px-1 py-0.5 text-xs" value={s.end} onChange={(e) => update(day, i, "end", e.target.value)} aria-label={`${label} shift ${i + 1} end`} />
                  <button type="button" onClick={() => removeShift(day, i)} className="text-slate-400 hover:text-rose-600" aria-label={`Remove ${label} shift ${i + 1}`}><Trash2 size={13} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <button type="button" className="text-xs font-semibold text-[#096dea] hover:underline" onClick={() => addShift(day)}>+ Shift</button>
              {days[day].length > 0 && day < 6 && (
                <button type="button" className="text-xs text-slate-500 hover:underline" onClick={() => copyToAll(day)} title="Copy to Monday–Saturday">Copy to Mon–Sat</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DateRangeRows({ botId, provider, schedules, onSaved }) {
  const rows = schedules.filter((s) => s.from_date).sort((a, b) => a.from_date.localeCompare(b.from_date));
  const [form, setForm] = useState({ kind: "off", from: "", to: "", fullDay: true, start: "10:00", end: "13:00", reason: "" });
  const [error, setError] = useState("");

  const add = async (e) => {
    e.preventDefault();
    setError("");
    const isOff = form.kind === "off";
    const withTimes = !isOff || !form.fullDay;
    try {
      await createSchedule(botId, provider.id, {
        is_available: !isOff,
        from_date: form.from,
        to_date: form.to || form.from,
        start_time: withTimes ? form.start : null,
        end_time: withTimes ? form.end : null,
        reason: form.reason.trim() || null,
      });
      setForm((f) => ({ ...f, from: "", to: "", reason: "" }));
      onSaved();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not add."));
    }
  };

  const remove = async (id) => {
    setError("");
    try {
      await deleteSchedule(botId, provider.id, id);
      onSaved();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not delete."));
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-bold text-slate-900">Time off &amp; extra hours</h4>
      <Alert>{error}</Alert>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-400">No leave or special hours.</p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <div>
                <span className={`mr-2 rounded px-1.5 py-0.5 text-[11px] font-bold ${r.is_available ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {r.is_available ? "EXTRA HOURS" : "TIME OFF"}
                </span>
                {formatDate(r.from_date)}
                {r.to_date !== r.from_date && ` → ${formatDate(r.to_date)}`}
                <span className="text-slate-500"> · {r.start_time ? `${hhmm(r.start_time)}–${hhmm(r.end_time)}` : "Full day"}</span>
                {r.reason && <span className="text-slate-500"> · {r.reason}</span>}
              </div>
              <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => remove(r.id)} aria-label="Delete row"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="grid grid-cols-2 gap-3 rounded-xl border border-dashed border-slate-300 p-3 sm:grid-cols-4">
        <Field label="Type">
          <select className={inputClass} value={form.kind} onChange={set("kind")}>
            <option value="off">Time off</option>
            <option value="extra">Extra hours</option>
          </select>
        </Field>
        <Field label="From"><input required type="date" className={inputClass} value={form.from} onChange={set("from")} /></Field>
        <Field label="To" hint="Blank = same day"><input type="date" className={inputClass} value={form.to} min={form.from} onChange={set("to")} /></Field>
        <Field label="Reason"><input className={inputClass} value={form.reason} onChange={set("reason")} placeholder="Leave, holiday…" /></Field>
        {form.kind === "off" && (
          <label className="col-span-2 flex items-center gap-2 text-xs text-slate-600 sm:col-span-4">
            <input type="checkbox" checked={form.fullDay} onChange={set("fullDay")} /> Full day(s)
          </label>
        )}
        {(form.kind === "extra" || !form.fullDay) && (
          <>
            <Field label="Start"><input required type="time" className={inputClass} value={form.start} onChange={set("start")} /></Field>
            <Field label="End"><input required type="time" className={inputClass} value={form.end} onChange={set("end")} /></Field>
          </>
        )}
        <div className="col-span-2 flex items-end justify-end sm:col-span-4">
          <button type="submit" className={btnSecondary}><Plus size={14} /> Add</button>
        </div>
      </form>
    </div>
  );
}

function ServicesOffered({ botId, provider, serviceIds, services, onSaved }) {
  const [selected, setSelected] = useState(serviceIds);
  const [message, setMessage] = useState({ kind: "", text: "" });
  useEffect(() => setSelected(serviceIds), [serviceIds]);
  if (!services.length) return null;

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    setMessage({ kind: "", text: "" });
    try {
      await setProviderServices(botId, provider.id, selected);
      setMessage({ kind: "info", text: "Services saved." });
      onSaved();
    } catch (err) {
      setMessage({ kind: "error", text: getSchedulingErrorMessage(err, "Could not save services.") });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-900">Services offered</h4>
        <button type="button" className={btnSecondary} onClick={save}>Save services</button>
      </div>
      <p className="text-xs text-slate-500">None selected means this provider offers every service.</p>
      <Alert kind={message.kind || "error"}>{message.text}</Alert>
      <div className="flex flex-wrap gap-2">
        {services.map((s) => (
          <label key={s.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm">
            <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
            {s.name}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function ProvidersPanel({ botId, providers, services, onProvidersChanged }) {
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (selectedId == null && providers.length) setSelectedId(providers[0].id);
  }, [providers, selectedId]);

  const loadDetail = useCallback(async () => {
    if (selectedId == null) return setDetail(null);
    try {
      setDetail(await getProvider(botId, selectedId));
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not load provider."));
    }
  }, [botId, selectedId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const save = async (values) => {
    const saved = editing?.id ? await updateProvider(botId, editing.id, values) : await createProvider(botId, values);
    setEditing(null);
    setSelectedId(saved.id);
    await onProvidersChanged();
    await loadDetail();
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
      <div className={`${card} space-y-3 !p-4`}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">Providers</h3>
          <button type="button" className={btnSecondary} onClick={() => setEditing({})}><Plus size={14} /> Add</button>
        </div>
        {providers.length === 0 && <p className="text-xs text-slate-400">No providers yet. Add a doctor, stylist or room.</p>}
        <ul className="space-y-1">
          {providers.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setSelectedId(p.id)}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm ${selectedId === p.id ? "bg-blue-50 text-[#096dea]" : "text-slate-700 hover:bg-slate-50"}`}
              >
                <UserRound size={15} />
                <span className="flex-1 truncate font-semibold">{p.name}</span>
                {!p.is_active && <span className="text-[10px] font-bold uppercase text-slate-400">Inactive</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className={`${card} space-y-6`}>
        <Alert>{error}</Alert>
        {!detail ? (
          <p className="text-sm text-slate-400">Select or add a provider.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{detail.name}</h3>
                <p className="text-xs text-slate-500">
                  {[detail.title, detail.phone, `${detail.slot_minutes}-min slots`].filter(Boolean).join(" · ")}
                  {!detail.is_active && " · not accepting bookings"}
                </p>
              </div>
              <button type="button" className={btnSecondary} onClick={() => setEditing(detail)}>Edit</button>
            </div>
            <WeeklyHoursEditor botId={botId} provider={detail} schedules={detail.schedules} onSaved={loadDetail} />
            <ServicesOffered botId={botId} provider={detail} serviceIds={detail.service_ids} services={services} onSaved={loadDetail} />
            <DateRangeRows botId={botId} provider={detail} schedules={detail.schedules} onSaved={loadDetail} />
          </>
        )}
      </div>

      <ProviderForm open={editing !== null} initial={editing} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}
